import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import MobileHeader from '../../components/mobile/MobileHeader';
import MobileBottomNav from '../../components/mobile/MobileBottomNav';
import { getProducts, getProductCategories } from '../../services/productsApi';
import type { ProductListItem, ProductCategory } from '../../types';
import styles from './MobileProductsListPage.module.css';

type SortOption = 'date' | 'price' | 'popularity' | 'name';

export const MobileProductsListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState<ProductListItem[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  // Parse filter state from URL
  const categoriesParam = searchParams.get('categories');
  const selectedCategories = useMemo(() =>
    categoriesParam ? categoriesParam.split(',') : [],
    [categoriesParam]
  );
  const minPrice = searchParams.get('minPrice') ? Number(searchParams.get('minPrice')) : undefined;
  const maxPrice = searchParams.get('maxPrice') ? Number(searchParams.get('maxPrice')) : undefined;
  const inStockOnly = searchParams.get('inStock') === 'true';
  const sortBy = (searchParams.get('sortBy') as SortOption) || 'date';
  const sortOrder = (searchParams.get('sortOrder') as 'asc' | 'desc') || 'desc';
  const searchParam = searchParams.get('search');
  const pageParam = searchParams.get('page');
  const currentPage = useMemo(() => pageParam ? parseInt(pageParam) : 1, [pageParam]);
  const limit = 20;
  const offset = (currentPage - 1) * limit;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      const [productsResponse, categoriesResponse] = await Promise.all([
        getProducts({
          categories: selectedCategories.length > 0 ? selectedCategories : undefined,
          search: searchParam || undefined,
          minPrice,
          maxPrice,
          inStock: inStockOnly || undefined,
          sortBy,
          sortOrder,
          limit,
          offset,
        }),
        getProductCategories(),
      ]);

      if (productsResponse.success) {
        setProducts(productsResponse.data);
        setTotal(productsResponse.pagination.total);
      }

      if (categoriesResponse.success) {
        setCategories(categoriesResponse.data);
      }

      setLoading(false);
    };

    fetchData();
  }, [selectedCategories, searchParam, minPrice, maxPrice, inStockOnly, sortBy, sortOrder, currentPage]);

  const updateURL = (updates: Record<string, string | null>) => {
    const newParams: Record<string, string> = {};
    if (searchParam) newParams.search = searchParam;
    if (selectedCategories.length > 0) newParams.categories = selectedCategories.join(',');
    if (minPrice) newParams.minPrice = String(minPrice);
    if (maxPrice) newParams.maxPrice = String(maxPrice);
    if (inStockOnly) newParams.inStock = 'true';
    if (sortBy !== 'date') newParams.sortBy = sortBy;
    if (sortOrder !== 'desc') newParams.sortOrder = sortOrder;
    newParams.page = '1';

    Object.entries(updates).forEach(([key, value]) => {
      if (value === null) {
        delete newParams[key];
      } else {
        newParams[key] = value;
      }
    });

    setSearchParams(newParams);
  };

  const handleProductClick = (productId: string) => {
    navigate(`/products/${productId}`);
  };

  const formatPrice = (price: number | null) => {
    if (price === null) return '$0';
    return `$${Math.round(price).toLocaleString()}`;
  };

  const totalPages = Math.ceil(total / limit);

  // Check if any filters are active
  const hasActiveFilters = Boolean(
    selectedCategories.length ||
    minPrice ||
    maxPrice ||
    inStockOnly
  );

  const handleClearFilters = () => {
    updateURL({
      categories: null,
      minPrice: null,
      maxPrice: null,
      inStock: null,
    });
  };

  return (
    <div className={styles.mobileProductsListPage}>
      <MobileHeader
        title="商品列表"
        showBack={false}
      />

      {/* Search */}
      <div className={styles.mobileProductsListPage__search}>
        <input
          type="text"
          placeholder="搜尋商品..."
          value={searchParam || ''}
          onChange={(e) => updateURL({ search: e.target.value || null })}
          className={styles.searchInput}
        />
      </div>

      {/* Sort and Filter Bar */}
      <div className={styles.mobileProductsListPage__bar}>
        <select
          value={sortBy}
          onChange={(e) => updateURL({ sortBy: e.target.value as SortOption })}
          className={styles.sortSelect}
        >
          <option value="date">最新上架</option>
          <option value="price">價格</option>
          <option value="popularity">人氣</option>
          <option value="name">名稱</option>
        </select>

        {hasActiveFilters && (
          <button
            className={styles.clearBtn}
            onClick={handleClearFilters}
          >
            ✕ 清除篩選
          </button>
        )}

        <button
          className={styles.filterBtn}
          onClick={() => setShowFilters(!showFilters)}
        >
          🔍 篩選
        </button>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className={styles.mobileProductsListPage__filters}>
          <h3>篩選條件</h3>

          {/* Categories */}
          <div className={styles.filterSection}>
            <h4>商品分類</h4>
            <div className={styles.categoryList}>
              {categories.map((cat) => (
                <label key={cat.id} className={styles.categoryItem}>
                  <input
                    type="checkbox"
                    checked={selectedCategories.includes(cat.name)}
                    onChange={(e) => {
                      const newCategories = e.target.checked
                        ? [...selectedCategories, cat.name]
                        : selectedCategories.filter(c => c !== cat.name);
                      updateURL({ categories: newCategories.length > 0 ? newCategories.join(',') : null });
                    }}
                  />
                  <span>{cat.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Stock Status */}
          <div className={styles.filterSection}>
            <label className={styles.checkboxItem}>
              <input
                type="checkbox"
                checked={inStockOnly}
                onChange={(e) => updateURL({ inStock: e.target.checked ? 'true' : null })}
              />
              <span>只顯示有庫存商品</span>
            </label>
          </div>

          <button
            className={styles.closeFilterBtn}
            onClick={() => setShowFilters(false)}
          >
            關閉
          </button>
        </div>
      )}

      {/* Product count */}
      <div className={styles.mobileProductsListPage__count}>
        共 {total} 件商品
      </div>

      {/* Product grid */}
      <div className={styles.mobileProductsListPage__content}>
        {loading ? (
          <div className={styles.mobileProductsListPage__loading}>載入中...</div>
        ) : products.length === 0 ? (
          <div className={styles.mobileProductsListPage__empty}>
            <p>找不到符合條件的商品</p>
          </div>
        ) : (
          <div className={styles.mobileProductsListPage__grid}>
            {products.map((product) => (
              <div
                key={product.id}
                className={styles.productCard}
                onClick={() => handleProductClick(product.id)}
              >
                {product.featuredImageUrl && (
                  <img
                    src={product.featuredImageUrl}
                    alt={product.title}
                    className={styles.productImage}
                  />
                )}
                <div className={styles.productInfo}>
                  <h3 className={styles.productTitle}>{product.title}</h3>
                  {product.shortDescription && (
                    <p className={styles.productDesc}>{product.shortDescription}</p>
                  )}
                  <div className={styles.productFooter}>
                    <span className={styles.productPrice}>
                      {formatPrice(product.salePrice || product.regularPrice)}
                    </span>
                    {product.stockStatus === 'outofstock' && (
                      <span className={styles.outOfStock}>已售完</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className={styles.mobileProductsListPage__pagination}>
            <button
              disabled={currentPage === 1}
              onClick={() => {
                const newParams: Record<string, string> = {};
                if (searchParam) newParams.search = searchParam;
                if (selectedCategories.length > 0) newParams.categories = selectedCategories.join(',');
                if (minPrice) newParams.minPrice = String(minPrice);
                if (maxPrice) newParams.maxPrice = String(maxPrice);
                if (inStockOnly) newParams.inStock = 'true';
                if (sortBy !== 'date') newParams.sortBy = sortBy;
                if (sortOrder !== 'desc') newParams.sortOrder = sortOrder;
                newParams.page = String(currentPage - 1);
                setSearchParams(newParams);
              }}
              className={styles.pageBtn}
            >
              上一頁
            </button>

            <span className={styles.pageInfo}>
              第 {currentPage} / {totalPages} 頁
            </span>

            <button
              disabled={currentPage === totalPages}
              onClick={() => {
                const newParams: Record<string, string> = {};
                if (searchParam) newParams.search = searchParam;
                if (selectedCategories.length > 0) newParams.categories = selectedCategories.join(',');
                if (minPrice) newParams.minPrice = String(minPrice);
                if (maxPrice) newParams.maxPrice = String(maxPrice);
                if (inStockOnly) newParams.inStock = 'true';
                if (sortBy !== 'date') newParams.sortBy = sortBy;
                if (sortOrder !== 'desc') newParams.sortOrder = sortOrder;
                newParams.page = String(currentPage + 1);
                setSearchParams(newParams);
              }}
              className={styles.pageBtn}
            >
              下一頁
            </button>
          </div>
        )}
      </div>

      <MobileBottomNav />
    </div>
  );
};

export default MobileProductsListPage;
